import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Alert,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Stack,
    Typography
} from "@mui/material";
import { FormEvent, useState } from "react";
import { doApiPost } from "../../api/Requests";
import { AuthBox, FlexRowBox, LeftContent, RightContent, StyledTextField } from "./Authentication.styles";
import BonisoftLogo from "../../common/ui/BonisoftLogo";
import AuthFooter from "./AuthFooter";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PasswordStrength, { passwordStrengthTest } from "./PasswordStrength";
import LanguageSwitcher from "../../common/ui/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import PasswordRules from "./PasswordRules";
import AuthLayout from "./AuthLayout";
import { GeneralButton } from "../../common/ui/GeneralButton";
import { ErrorCode } from "../../api/errorCode";

interface ResetState {
    password: string,
    passwordRepeated: string,
    email: string,
}

/**
 * This screen handles the password reset link. It uses the url-provided token and prompts the
 * user for the email and new password. After the password reset the user gets redirected to
 * the login page where he can log in normally.
 */
export default function LinkingResetPasswordScreen() {
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatedPassword, setShowRepeatedPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0)
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorKey, setErrorKey] = useState<string>("");
    const { t } = useTranslation();
    const token = searchParams.get("token");
    const urlEmail = searchParams.get("email");

    const [resetState, setResetState] = useState<ResetState>({
        password: "",
        passwordRepeated: "",
        email: urlEmail ?? "",
    });
    
    const isPasswordMatch = resetState.password === resetState.passwordRepeated;
    const isFormValid = isPasswordMatch && resetState.password.length > 0 && passwordStrength >= 3;

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowRepeatedPassword = () => setShowRepeatedPassword((show) => !show);

    const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const onHandleResetPassword = async function (event: FormEvent) {
        // We use a form with preventDefault as button click listener. This is needed to
        // leverage the browsers built in save password feature
        event.preventDefault()

        setIsLoading(true);

        const result = await doApiPost<any, never>(
            `/api/users/password_reset/${token}`,
            {
                eMail: resetState.email,
                newPassword: resetState.password
            },
            { handleUnauthenticated: true }
        );

        if (result.success()) {
            navigate("/")
        } else {
            setIsError(true);
            if (result.errorResponse?.errorCode === ErrorCode.AuthenticationTokenInvalid) {
                setErrorKey("authentication.authSettings.linkExpired");
            }
            else {
                setErrorKey("authentication.authSettings.passwordResetFailed");
            }
        }
        setIsLoading(false);
    }

    return (
        <AuthLayout>
            <AuthBox component="form" onSubmit={onHandleResetPassword}>
                <FlexRowBox>
                    <LeftContent>
                        <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h5">
                                    {t("authentication.authSettings.resetPassword")}
                                </Typography>
                                <LanguageSwitcher />
                            </Stack>

                            <StyledTextField
                                label={t("authentication.authSettings.email")}
                                fullWidth
                                value={resetState.email}
                                InputProps={{
                                    readOnly: true,
                                }}
                                disabled={true}
                                variant="outlined"
                            />

                            <OutlinedInput
                                required
                                fullWidth
                                placeholder={t("authentication.authSettings.password")}
                                type={showPassword ? "text" : "password"}
                                value={resetState.password}
                                onChange={(e) => {
                                    setPasswordStrength(passwordStrengthTest(e.target.value));
                                    setResetState({ ...resetState, password: e.target.value });
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDown}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />

                            <PasswordStrength strength={passwordStrength} />
                            <PasswordRules pw={resetState.password} />

                            <OutlinedInput
                                required
                                fullWidth
                                placeholder={t("authentication.authSettings.repeatPassword")}
                                type={showRepeatedPassword ? "text" : "password"}
                                value={resetState.passwordRepeated}
                                error={resetState.password !== resetState.passwordRepeated}
                                onChange={(e) =>
                                    setResetState({ ...resetState, passwordRepeated: e.target.value })
                                }
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleClickShowRepeatedPassword}
                                            onMouseDown={handleMouseDown}
                                            edge="end"
                                        >
                                            {showRepeatedPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />

                            {isError && (
                                <Alert severity="error">
                                    {t(errorKey)}
                                </Alert>
                            )}

                            <GeneralButton
                                fullWidth
                                type="submit"
                                appVariant="primary"
                                loading={isLoading}
                                disabled={!isFormValid}
                            >
                                {t("authentication.authSettings.setPassword")}
                            </GeneralButton>
                        </Stack>
                    </LeftContent>
                    <RightContent>
                        <BonisoftLogo imgRatio={30} />
                    </RightContent>
                </FlexRowBox>
            </AuthBox>
            <AuthFooter />
        </AuthLayout>
    );
}
